/**
 * CalendarPageErrorStates Component
 * 
 * Purpose:
 * Error states component that displays various error and empty state banners for the calendar page.
 * Handles three scenarios: reservations fetch error, no property selected/load error, and properties
 * loading timeout. Each state shows appropriate messaging and retry actions.
 * 
 * High-level Workflow:
 * 1. Receives error states, loading states, and handlers as props.
 * 2. Conditionally renders three error/empty state banners:
 *    - Reservations error: Shows when reservationsError exists and not loading
 *    - No property selected: Shows when !hasProperty && !propertiesLoading
 *    - Properties timeout: Shows when propertiesLoading && showPropertiesTimeout
 * 3. Each banner provides appropriate messaging and retry button.
 * 4. Applies mobile/desktop styling based on isMobile prop.
 * 
 * Assumptions & Invariants:
 * - Reservations error: Only shows when reservationsError exists AND reservationsLoading is false
 *   (prevents showing error during active fetch). Error message displays reservationsError string.
 *   Retry button reloads the page.
 * - No property selected: Shows when no property is selected (!hasProperty) AND properties are
 *   not loading (!propertiesLoading). Handles three sub-states:
 *   - propertiesError: Shows error message with retry button (calls retryProperties or reloads)
 *   - availableProperties.length === 0: Shows "No properties available" message
 *   - Otherwise: Shows "Please select a property to view calendar." message
 * - Properties timeout: Shows when propertiesLoading is true for more than 10 seconds
 *   (showPropertiesTimeout === true). Provides retry button that calls retryProperties or reloads.
 * - Retry behavior: retryProperties handler is optional. If not provided, falls back to
 *   window.location.reload().
 * - Styling: All banners use getErrorBannerStyle(isMobile) for responsive styling.
 * 
 * Dependencies:
 * - Internal:
 *   - None (pure presentational component)
 * - External:
 *   - react: None (functional component, no hooks)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 5 refactoring)
 */

/**
 * Error states component
 * 
 * @param {boolean} isMobile - Whether mobile layout is active
 * @param {boolean} showReservationsError - Whether to show reservations error banner
 * @param {string|null} reservationsError - Reservations fetch error message
 * @param {boolean} hasProperty - Whether a property is selected
 * @param {boolean} propertiesLoading - Whether properties are loading
 * @param {Error|null} propertiesError - Properties fetch error object
 * @param {Array} availableProperties - Array of available properties
 * @param {boolean} showPropertiesTimeout - Whether to show properties timeout banner
 * @param {Function|null} retryProperties - Handler to retry properties fetch (optional)
 * @param {Object} styles - Styles object from CalendarPage
 */
export default function CalendarPageErrorStates({
  isMobile,
  showReservationsError,
  reservationsError,
  hasProperty,
  propertiesLoading,
  propertiesError,
  availableProperties,
  showPropertiesTimeout,
  retryProperties,
  styles,
}) {
  return (
    <>
      {/* Error state (if reservations failed) */}
      {showReservationsError && (
        <div style={styles.getErrorBannerStyle(isMobile)}>
          <div className="owner-alert owner-alert-error" style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}>
            <strong>Error loading calendar data</strong>
            <p>{reservationsError}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-4)', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* No property selected state - show retry button if properties failed to load */}
      {!hasProperty && !propertiesLoading && (
        <div style={styles.getErrorBannerStyle(isMobile)}>
          <div className="owner-alert owner-alert-info" style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}>
            {propertiesError ? (
              <>
                <strong>Couldn't load property</strong>
                <p>{propertiesError.message || 'Failed to load properties. Please try again.'}</p>
                <button 
                  onClick={() => (retryProperties ? retryProperties() : window.location.reload())} 
                  style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-4)', cursor: 'pointer' }}
                >
                  Retry
                </button>
              </>
            ) : availableProperties.length === 0 ? (
              <>
                <strong>No properties available</strong>
                <p>Please contact support if you believe this is an error.</p>
              </>
            ) : (
              'Please select a property to view calendar.'
            )}
          </div>
        </div>
      )}
      
      {/* Timeout escape hatch - if propertiesLoading is true for too long, show retry option */}
      {propertiesLoading && showPropertiesTimeout && (
        <div style={styles.getErrorBannerStyle(isMobile)}>
          <div className="owner-alert owner-alert-warning" style={{ margin: 'var(--spacing-4)', padding: 'var(--spacing-4)' }}>
            <strong>Taking longer than expected</strong>
            <p>Property loading is taking longer than usual. You can retry without refreshing.</p>
            <button 
              onClick={() => (retryProperties ? retryProperties() : window.location.reload())} 
              style={{ marginTop: 'var(--spacing-2)', padding: 'var(--spacing-2) var(--spacing-4)', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </>
  );
}
